import React from 'react';
import { Badge } from '@/components/ui/badge';
import { TrendingUp, Award, Star, Crown, Gem, Building } from 'lucide-react';
import type { EmployeeGrade } from '@/types/timesheet';
import { cn } from '@/lib/utils';

interface EmployeeGradeBadgeProps {
  grade?: EmployeeGrade;
  size?: 'sm' | 'md' | 'lg';
  showIcon?: boolean;
  className?: string;
}

export const EmployeeGradeBadge: React.FC<EmployeeGradeBadgeProps> = ({
  grade,
  size = 'md',
  showIcon = true,
  className,
}) => {
  if (!grade) return null;

  const getGradeConfig = (grade: EmployeeGrade) => {
    switch (grade) {
      case 'Junior':
        return {
          icon: TrendingUp,
          label: 'Junior',
          className: 'bg-success-100 text-success-700 border-success-200 dark:bg-success-900/30 dark:text-success-300 dark:border-success-800',
        };
      case 'Mid-Level':
        return {
          icon: Award,
          label: 'Mid-Level',
          className: 'bg-info-100 text-info-700 border-info-200 dark:bg-info-900/30 dark:text-info-300 dark:border-info-800',
        };
      case 'Senior':
        return {
          icon: Star,
          label: 'Senior',
          className: 'bg-blueviolet-100 text-blueviolet-700 border-blueviolet-200 dark:bg-blueviolet-900/30 dark:text-blueviolet-300 dark:border-blueviolet-800',
        };
      case 'Lead':
        return {
          icon: Crown,
          label: 'Lead',
          className: 'bg-warning-100 text-warning-700 border-warning-200 dark:bg-warning-900/30 dark:text-warning-300 dark:border-warning-800',
        };
      case 'Principal':
        return {
          icon: Gem,
          label: 'Principal',
          className: 'bg-purple-100 text-purple-700 border-purple-200 dark:bg-purple-900/30 dark:text-purple-300 dark:border-purple-800',
        };
      case 'Director':
        return {
          icon: Building,
          label: 'Director',
          className: 'bg-neutral-900 text-white border-neutral-800 dark:bg-neutral-100 dark:text-neutral-900 dark:border-neutral-200',
        };
      default:
        return {
          icon: Award,
          label: grade,
          className: 'bg-neutral-100 text-neutral-700 border-neutral-200 dark:bg-neutral-800 dark:text-neutral-300 dark:border-neutral-700',
        };
    }
  };

  const config = getGradeConfig(grade);
  const Icon = config.icon;

  const sizeClasses = {
    sm: 'text-xs h-5',
    md: 'text-xs h-6',
    lg: 'text-sm h-7',
  };

  const iconSizeClasses = {
    sm: 'h-3 w-3',
    md: 'h-3.5 w-3.5',
    lg: 'h-4 w-4',
  };

  return (
    <Badge
      variant="outline"
      className={cn(
        'font-medium',
        sizeClasses[size],
        config.className,
        className
      )}
    >
      {showIcon && <Icon className={cn(iconSizeClasses[size], 'mr-1')} />}
      {config.label}
    </Badge>
  );
};
